import { Map, fromJS } from "immutable";
import {
	fileStatusConstants,
	snackbarConstants,
	snackbarTypeConstants,
} from "../constants/constants";
import {
	getItemFromLocalStorage,
	removeItemFromLocalStorage,
	removeItemFromSessionStorage,
	setItemToLocalStorage,
	setItemToSessionStorage,
} from "../utils/local-storage.util";
import localize, { LocKeys } from "../constants/localizations";

/**
 * sessionStorage key for the per-tab "this tab initiated the active extraction"
 * marker. Survives refresh, dies with the tab, never crosses tabs (do not move
 * to localStorage — that defeats the gate).
 */
export const ACTIVE_EXTRACTION_JOB_ID_KEY = "bimifyActiveExtractionJobId";

/**
 * Placeholder progress used when restoring a per-level C2I completion
 * (percentage=100 without isReady). The overall pipeline is still running;
 * 10% lets the listener remount and pick up subsequent events without
 * displaying a misleading "100%".
 */
const RESTORE_INTERMEDIATE_PROGRESS = 10;

/**
 * True when the given socket event's jobId matches the tab's recorded
 * activeExtractionJobId. sessionStorage rehydration produces strings while
 * live state holds numbers, so always stringify both sides.
 *
 * @param {number|string|null|undefined} activeExtractionJobId
 * @param {number|string|null|undefined} jobId
 */
export const isExtractionInitiator = (activeExtractionJobId, jobId) => {
	if (activeExtractionJobId == null || jobId == null) return false;
	return String(activeExtractionJobId) === String(jobId);
};

/**
 * Global snackbar reducer redux
 */

const initialState = Map({
	open: false,
	type: null,
	files: Map({}),
	message: null,
	severity: null,
	bimifyProgress: Map({
		inProgress: false,
		progress: 1,
		message: "",
		canCancelApsJob: false,
	}),
	activeExtractionJobId: null,
	zipName: "",
});

//

export default (state = initialState, action) => {
	/**
	 * Update value for upload file progress
	 * @param {*} files - updated value
	 * @returns
	 */
	const updateUploadProgress = (files) => {
		const oldFiles = state.get("files");
		const oldFilesJS = oldFiles.size > 0 ? oldFiles.toJS() : [];

		files.forEach((updateFile) => {
			const index = oldFilesJS.findIndex(
				(oldFile) => oldFile.id === updateFile.id
			);

			if (index !== -1) {
				oldFilesJS[index].value = updateFile.value;
				if (updateFile.value === 100) {
					oldFilesJS[index].status = fileStatusConstants.COMPLETED;
				}
			}
		});

		const inProgress = oldFilesJS.find(
			(file) => file.status === fileStatusConstants.IN_PROGRESS
		);
		if (!inProgress) {
			return state.set("open", false).set("type", null).set("files", Map());
		}

		return state.set("files", fromJS(oldFilesJS));
	};

	/**
	 * Set initial value for FILES in state
	 * @param {*} files - initial value
	 * @returns
	 */
	const setFiles = (files) => {
		const oldFiles = state.get("files");
		const oldFilesJS = oldFiles ? oldFiles.toJS() : [];
		const newFiles = [];

		oldFilesJS.length > 0 &&
			oldFilesJS.forEach((file) => {
				newFiles.push(file);
			});

		files.forEach((file) => {
			const exist = newFiles.some((newFile) => newFile.id === file.id);

			if (!exist)
				newFiles.push({
					id: file.id,
					name: file.name,
					size: file.size,
					value: file.value,
					source: file.source,
					status: fileStatusConstants.IN_PROGRESS,
				});
		});

		return state.set("files", fromJS(newFiles));
	};

	/**
	 * Remove single file
	 * @param {*} fileId - file to be removed
	 * @returns
	 */
	const removeFile = (fileId) => {
		const oldFiles = state.get("files");
		const oldFilesJS = oldFiles ? oldFiles.toJS() : [];

		const newFiles = oldFilesJS.filter((file) => file.id !== fileId);

		const filesLength = newFiles.length > 0;

		return filesLength
			? state.set("files", fromJS(newFiles))
			: state.set("open", false).set("type", null).set("files", Map());
	};

	/**
	 * Cancel API request for upload file
	 * @param {*} fileId - file to be canceled upload
	 * @returns
	 */
	const cancelUploadRequest = (fileId) => {
		const oldFiles = state.get("files");
		let oldFilesJS = oldFiles ? oldFiles.toJS() : [];
		const index = oldFilesJS.findIndex((file) => file.id === fileId);

		if (index !== -1) {
			if (oldFilesJS[index].status !== fileStatusConstants.CANCELED) {
				oldFilesJS[index].source.cancel();
				oldFilesJS[index].status = fileStatusConstants.CANCELED;
			}
		}

		return state.set("files", fromJS(oldFilesJS));
	};

	/**
	 * Remove all files from state and CANCEL API request
	 * @returns
	 */
	const removeFiles = () => {
		const files = state.get("files");
		let filesJS = files ? files.toJS() : [];

		filesJS.forEach((file) => {
			if (file.status !== fileStatusConstants.CANCELED) {
				file.source.cancel();
			}
		});
		return state.set("files", Map());
	};

	const updateBimifyProgress = (data) => {
		const {
			open = true,
			type = snackbarTypeConstants.BIMIFY_PROGRESS,
			jobId,
			jobStatusId,
			progress = 1,
			message = "",
			inProgress,
			errorResponse = false,
			successResponse = false,
			canCancelApsJob,
		} = data;

		const tempData = {
			jobId,
			jobStatusId,
			progress,
			message,
			inProgress,
			open,
			type,
			errorResponse,
			successResponse,
			canCancelApsJob:
				canCancelApsJob || state.getIn(["bimifyProgress", "canCancelApsJob"]),
		};
		setItemToLocalStorage("bimifyProgress", JSON.stringify(tempData));
		return state
			.set("open", open)
			.set("type", type)
			.setIn(["bimifyProgress", "progress"], progress)
			.setIn(["bimifyProgress", "message"], message)
			.setIn(["bimifyProgress", "inProgress"], inProgress)
			.setIn(["bimifyProgress", "jobId"], jobId)
			.setIn(["bimifyProgress", "jobStatusId"], jobStatusId)
			.setIn(["bimifyProgress", "errorResponse"], errorResponse)
			.setIn(["bimifyProgress", "successResponse"], successResponse)
			.setIn(
				["bimifyProgress", "canCancelApsJob"],
				canCancelApsJob || state.getIn(["bimifyProgress", "canCancelApsJob"])
			);
	};

	const updateExtractProgress = (data) => {
		const {
			open = true,
			type = snackbarTypeConstants.EXTRACT_PROGRESS,
			jobId,
			tempProgress = 0,
			totalLevels,
			disciplineId,
			completeExtraction = false,
			buildingLevels = [],
			buildingId,
			progress = 1,
			message = "",
			inProgress,
			errorResponse = false,
			successResponse = false,
			isBimifyExtraction = false,
			pointCloudFileId = null,
		} = data;

		return state
			.set("open", open)
			.set("type", type)
			.setIn(["extractProgress", "completeExtraction"], completeExtraction)
			.setIn(["extractProgress", "totalLevels"], totalLevels)
			.setIn(["extractProgress", "tempProgress"], tempProgress)
			.setIn(["extractProgress", "buildingLevels"], buildingLevels)
			.setIn(["extractProgress", "progress"], progress)
			.setIn(["extractProgress", "message"], message)
			.setIn(["extractProgress", "inProgress"], inProgress)
			.setIn(["extractProgress", "jobId"], jobId)
			.setIn(["extractProgress", "disciplineId"], disciplineId)
			.setIn(["extractProgress", "buildingId"], buildingId)
			.setIn(["extractProgress", "errorResponse"], errorResponse)
			.setIn(["extractProgress", "successResponse"], successResponse)
			.setIn(["extractProgress", "isBimifyExtraction"], isBimifyExtraction)
			.setIn(["extractProgress", "pointCloudFileId"], pointCloudFileId);
	};

	const updateExtractProgressLabelingTool = (data) => {
		const { action } = data;
		// Get current extractProgress from Redux state instead of localStorage
		const extractProgress = state.get("extractProgress")
			? state.get("extractProgress").toJS()
			: {};

		let clearGate = false;
		if (action) {
			extractProgress.progress = 95;
			extractProgress.message = localize(LocKeys.CREATE_XML_FILE);
		} else {
			// Only close the widget if all levels have been processed.
			// AutomateLabeling fires per-level, but other levels may still be in progress.
			const remainingLevels = extractProgress.buildingLevels || [];
			if (remainingLevels.length === 0) {
				extractProgress.progress = 100;
				extractProgress.tempProgress = 0;
				extractProgress.open = false;
				extractProgress.type = null;
				extractProgress.inProgress = false;
				extractProgress.successResponse = true;
				// Non-maverick labeling completion is a terminal state. Clear the
				// initiator gate so AutomateLabelingTool unmounts and the XML
				// upload does not re-fire on the next socket reconnect.
				clearGate = true;
			} else {
				// More levels pending — update message but keep widget open
				extractProgress.message = data.message || extractProgress.message;
			}
		}

		const next = updateExtractProgress(extractProgress);
		if (clearGate) {
			removeItemFromSessionStorage(ACTIVE_EXTRACTION_JOB_ID_KEY);
			return next.set("activeExtractionJobId", null);
		}
		return next;
	};

	/**
	 * Restore progress state from API response
	 * Maps API record fields to extractProgress state shape
	 * @param {Object} data - API response with records array
	 * @returns {Map} Updated state
	 */
	const restoreCad2ImageProgress = (data) => {
		const { records = [] } = data;

		// Don't overwrite active in-progress state with stale API data
		// This prevents a race condition where clicking "Bimify" sets inProgress=true,
		// then the progress component mounts and fetches old completed progress from the API,
		// which would overwrite the fresh state and close the widget.
		// Also block when a fresh terminal state has just landed (success/error from
		// a live socket event) so a lagging API record can't resurrect "still running".
		const currentInProgress = state.getIn(["extractProgress", "inProgress"]);
		const currentSuccess = state.getIn(["extractProgress", "successResponse"]);
		const currentError = state.getIn(["extractProgress", "errorResponse"]);
		if (currentInProgress || currentSuccess || currentError) {
			return state;
		}

		// No records means no progress to restore
		if (!records || records.length === 0) {
			return state;
		}

		// Find the most relevant progress record.
		// Only restore extraction-style progress records; ignore BIMIFY/job-status records.
		// Records are ordered by updatedAt from API, so first matching record is most recent.
		const allowedEventTypes = new Set(["cad2image", "ipserver", "pc"]);
		const progressRecord = records.find((record) => {
			if (!record || !record.socketData) return false;
			const socketData = record.socketData;

			// BIMIFY completion / APS upload markers should never restore extract progress
			if (socketData.uploadedRevitFile) return false;

			// BIMIFY job-status records (have currentStatusNumber/statusesTotal, no percentage)
			const isBimifyJobStatusRecord =
				record.eventType === "job-status" &&
				socketData.currentStatusNumber != null &&
				socketData.statusesTotal != null &&
				socketData.percentage == null;
			if (isBimifyJobStatusRecord) return false;

			// Extraction records either have a known eventType, or explicitly include a percentage field
			return (
				allowedEventTypes.has(record.eventType) || socketData.percentage != null
			);
		});

		// No extraction progress found in records; do not open the widget or show 0%
		if (!progressRecord) {
			return state;
		}

		if (!progressRecord || !progressRecord.socketData) {
			return state;
		}

		const socketData = progressRecord.socketData;
		const {
			id,
			jobId: socketJobId,
			percentage = 0,
			message = "",
			isError = false,
			error = false,
			isReady = false,
			isIpServer = false,
			disciplineId,
			buildingId,
			buildingLevels = [],
			tempProgress = 0,
			totalLevels = 1,
			isBimifyExtraction = false,
			pointCloudFileId = null,
		} = socketData;

		if (percentage === undefined || percentage === null) {
			return state;
		}

		// API progress records use "id" for the job ID, websocket events use "jobId"
		// During IPserver phase, `id` is the level file ID, NOT the job ID.
		// Use requestedJobId (from API request) or preserve existingJobId for IPserver messages.
		const existingJobId = state.getIn(["extractProgress", "jobId"]);
		const { requestedJobId } = data;
		const jobId =
			requestedJobId ||
			socketJobId ||
			(isIpServer ? existingJobId : id) ||
			existingJobId;

		// Determine progress state based on socketData
		let inProgress = true;
		let errorResponse = false;
		let successResponse = false;
		let progress = Number(percentage) || 0;

		// Upstream PC/PTS worker uses the legacy `error` key; IP Server uses `isError`.
		// Treat either as an error signal so a restored error record opens the widget
		// in a dismissible error state instead of a stuck "in progress" one.
		const hasError = Boolean(isError) || Boolean(error);

		if (hasError) {
			inProgress = false;
			errorResponse = true;
			progress = 100;
		} else if (isReady) {
			// Explicitly completed — nothing to restore.
			// The user can start a new job; showing stale "completed" state is confusing.
			//
			// Only treat IP-server isReady as the FINAL signal that clears the gate.
			// cad-2-image emits its own isReady ("Job completed.") well before
			// IP-server runs; if the user refreshes in that window and restore
			// matches that record, clearing the gate would unmount
			// AutomateLabelingTool and the subsequent `job-ready-for-labeling-tool`
			// event would arrive at nothing.
			if (isIpServer) {
				const activeJobId = state.get("activeExtractionJobId");
				if (activeJobId != null && String(activeJobId) === String(jobId)) {
					removeItemFromSessionStorage(ACTIVE_EXTRACTION_JOB_ID_KEY);
					return state.set("activeExtractionJobId", null);
				}
			}
			return state;
		} else if (progress >= 100) {
			// Per-level C2I completion (percentage=100 without isReady).
			// The overall pipeline may still be in progress — restore at an
			// intermediate value so the ExtractionProgressListener mounts and
			// picks up new events without showing a misleading 100%.
			progress = RESTORE_INTERMEDIATE_PROGRESS;
		}

		// Determine if snackbar should be open
		const shouldOpen = inProgress || errorResponse;
		const type = shouldOpen ? snackbarTypeConstants.EXTRACT_PROGRESS : null;

		// Preserve existing disciplineId/buildingId from state if not present in socketData
		// This handles the case where restored progress lacks these fields
		const existingDisciplineId = state.getIn([
			"extractProgress",
			"disciplineId",
		]);
		const existingBuildingId = state.getIn(["extractProgress", "buildingId"]);

		return state
			.set("open", shouldOpen)
			.set("type", type)
			.setIn(["extractProgress", "completeExtraction"], successResponse)
			.setIn(["extractProgress", "totalLevels"], totalLevels)
			.setIn(["extractProgress", "tempProgress"], tempProgress)
			.setIn(["extractProgress", "buildingLevels"], buildingLevels)
			.setIn(["extractProgress", "progress"], progress)
			.setIn(["extractProgress", "message"], message)
			.setIn(["extractProgress", "inProgress"], inProgress)
			.setIn(["extractProgress", "jobId"], jobId)
			.setIn(
				["extractProgress", "disciplineId"],
				disciplineId || existingDisciplineId
			)
			.setIn(
				["extractProgress", "buildingId"],
				buildingId || existingBuildingId
			)
			.setIn(["extractProgress", "errorResponse"], errorResponse)
			.setIn(["extractProgress", "successResponse"], successResponse)
			.setIn(["extractProgress", "isBimifyExtraction"], isBimifyExtraction)
			.setIn(["extractProgress", "pointCloudFileId"], pointCloudFileId);
	};

	switch (action.type) {
		case snackbarConstants.OPEN:
			return state.set("open", true).set("type", action.data);

		case snackbarConstants.CLOSE:
			return state.set("open", false).set("type", null);

		case snackbarConstants.SET_FILES_DATA:
			return setFiles(action.data);

		case snackbarConstants.REMOVE_FILE:
			return removeFile(action.data);

		case snackbarConstants.CANCEL_UPLOAD_REQUEST:
			return cancelUploadRequest(action.data);

		case snackbarConstants.UPDATE_UPLOAD_PROGRESS:
			return updateUploadProgress(action.data);

		case snackbarConstants.REMOVE_FILES:
			return removeFiles();

		case snackbarConstants.UPDATE_BIMIFY_PROGRESS:
			return updateBimifyProgress(action.data);

		case snackbarConstants.UPDATE_EXTRACT_PROGRESS:
			return updateExtractProgress(action.data);

		case snackbarConstants.UPDATE_EXTRACT_PROGRESS_LABELING_TOOL:
			return updateExtractProgressLabelingTool(action.data);

		case snackbarConstants.RESTORE_CAD_2_IMAGE_PROGRESS:
			return restoreCad2ImageProgress(action.data);

		case snackbarConstants.SET_ACTIVE_EXTRACTION_JOB_ID: {
			const jobId = action.data ?? null;
			if (jobId !== null) {
				setItemToSessionStorage(ACTIVE_EXTRACTION_JOB_ID_KEY, String(jobId));
			} else {
				removeItemFromSessionStorage(ACTIVE_EXTRACTION_JOB_ID_KEY);
			}
			return state.set("activeExtractionJobId", jobId);
		}

		case snackbarConstants.CLEAR_ACTIVE_EXTRACTION_JOB_ID:
			removeItemFromSessionStorage(ACTIVE_EXTRACTION_JOB_ID_KEY);
			return state.set("activeExtractionJobId", null);

		case snackbarConstants.CANCEL_BIMIFY_PROGRESS:
			const bimifyProgress = getItemFromLocalStorage("bimifyProgress")
				? JSON.parse(getItemFromLocalStorage("bimifyProgress"))
				: null;

			const cancelTempData = {
				...bimifyProgress,
				canCancelApsJob: action.data,
			};

			setItemToLocalStorage("bimifyProgress", JSON.stringify(cancelTempData));

			return state.setIn(["bimifyProgress", "canCancelApsJob"], action.data);

		case snackbarConstants.RESET_BIMIFY_PROGRESS:
			removeItemFromLocalStorage("bimifyProgress");
			return state
				.set("open", false)
				.set("type", null)
				.set("bimifyProgress", initialState?.get("bimifyProgress"));

		case snackbarConstants.SET_ZIP_NAME:
			return state.set("zipName", action.data);

		case snackbarConstants.SHOW_MESSAGE:
			return state
				.set("message", action.data?.message || null)
				.set("severity", action.data?.severity || null)
				.set("open", true);

		default:
			return state;
	}
};
